import { useMemo } from 'react';
import { useGlobalStore } from '@digiwedge/global-state';

type Claims = {
  scope?: string;
  roles?: string[];
  permissions?: string[];
};

function decodeJwt(token?: string | null): Claims {
  if (!token) return {};
  try {
    const parts = token.split('.');
    if (parts.length < 2) return {};
    const json = JSON.parse(atob(parts[1]));
    return {
      scope: typeof json.scope === 'string' ? json.scope : undefined,
      roles: Array.isArray(json.roles) ? (json.roles as string[]) : undefined,
      permissions: Array.isArray(json.permissions) ? (json.permissions as string[]) : undefined,
    } satisfies Claims;
  } catch {
    return {};
  }
}

export function usePermissions() {
  const token = useGlobalStore((s) => s.token);
  const claims = useMemo(() => decodeJwt(token), [token]);
  const scopeSet = useMemo(
    () => new Set((claims.scope ?? '').split(/\s+/).filter(Boolean)),
    [claims.scope],
  );
  const rolesSet = useMemo(() => new Set(claims.roles ?? []), [claims.roles]);
  const permsSet = useMemo(() => new Set(claims.permissions ?? []), [claims.permissions]);

  const has = (perm: string): boolean => {
    // direct permission
    if (permsSet.has(perm)) return true;
    // scope string contains perm
    if (scopeSet.has(perm)) return true;
    // special-case admin
    if (perm === 'cmp.admin' && (rolesSet.has('CMP_ADMIN') || rolesSet.has('cmp.admin')))
      return true;
    return false;
  };

  return { has, claims };
}
